import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, User, Users, Target, Newspaper, CalendarDays,
  Phone, LogIn, Menu, GraduationCap, LayoutGrid,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";
import { resolveImg } from "@/lib/api";
import Footer from "@/components/Footer";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "dashboard", end: true },
  { to: "/profil", label: "Profil", icon: User, testid: "profile" },
  { to: "/guru", label: "Daftar Guru", icon: Users, testid: "teachers" },
  { to: "/visi-misi", label: "Visi & Misi", icon: Target, testid: "vision" },
  { to: "/berita", label: "Berita", icon: Newspaper, testid: "news" },
  { to: "/kegiatan", label: "Kegiatan", icon: CalendarDays, testid: "activities" },
  { to: "/galeri", label: "Galeri", icon: LayoutGrid, testid: "gallery" },
  { to: "/kontak", label: "Kontak", icon: Phone, testid: "contact" },
];

const Brand = ({ logo }) => (
  <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-100">
    <img
      src={resolveImg(logo)}
      alt="Logo MI Futuhiyyah"
      className="h-12 w-12 rounded-full object-contain bg-white ring-2 ring-emerald-100"
    />
    <div className="leading-tight">
      <p className="font-heading font-extrabold text-emerald-800 text-[15px]">MI Futuhiyyah</p>
      <p className="text-[11px] text-slate-500 font-medium">Doro - Pekalongan</p>
    </div>
  </div>
);

const NavList = ({ onNavigate }) => (
  <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
    {navItems.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        data-testid={`sidebar-nav-${item.testid}-link`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200 ${
            isActive
              ? "bg-emerald-50 text-emerald-800 font-semibold shadow-sm border-r-4 border-emerald-600"
              : "text-slate-600 hover:bg-slate-50 hover:text-emerald-700"
          }`
        }
      >
        <item.icon className="h-[18px] w-[18px]" />
        {item.label}
      </NavLink>
    ))}
  </nav>
);

const PublicLayout = () => {
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-slate-50/60">
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-slate-200 bg-white shadow-sm md:flex lg:w-72">
        <Brand logo={settings.logo_url} />
        <NavList />
        <div className="border-t border-slate-100 p-3 space-y-2">
          <Button
            data-testid="sidebar-nav-admin-login-button"
            variant="outline"
            className="w-full justify-start gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={() => navigate("/login")}
          >
            <LogIn className="h-4 w-4" /> Login Admin
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-50 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-md md:hidden">
        <div className="flex items-center gap-2">
          <img src={resolveImg(settings.logo_url)} alt="Logo" className="h-9 w-9 rounded-full object-contain ring-2 ring-emerald-100" />
          <div className="leading-tight">
            <p className="font-heading font-bold text-emerald-800 text-sm">MI Futuhiyyah</p>
            <p className="text-[10px] text-slate-500">Doro - Pekalongan</p>
          </div>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button data-testid="mobile-menu-button" variant="ghost" size="icon" className="text-emerald-800">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 flex flex-col">
            <Brand logo={settings.logo_url} />
            <NavList onNavigate={() => setOpen(false)} />
            <div className="border-t border-slate-100 p-3">
              <Button
                data-testid="mobile-admin-login-button"
                variant="outline"
                className="w-full justify-start gap-2 border-emerald-200 text-emerald-700"
                onClick={() => { setOpen(false); navigate("/login"); }}
              >
                <LogIn className="h-4 w-4" /> Login Admin
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      {/* Main content */}
      <main className="md:ml-64 lg:ml-72 min-h-screen flex flex-col">
        <div className="flex-1">
          <Outlet key={location.pathname} />
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default PublicLayout;
